import {
  Box,
  Button,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  styled,
  useTheme,
} from "@mui/material"
import { useCommitteeStatus } from "@pharmupp/p3-employee-committee-domain"
import {
  ArrowDownRightIcon,
  ButtonLink,
  pluralize,
  useMyAdherentSummary,
  useSsoService,
  useStepUpToken,
} from "@pharmupp/p3-front-commons"
import { useMemo } from "react"
import { ChevronRight, User } from "react-feather"
import { Info, Lock, Unlock } from "react-feather"
import { useNavigate } from "react-router-dom"
import {
  AdherentCommitteeIcon,
  AdherentCommitteeLink,
} from "./AdherentCommitteeLink"
import { UserMenuLayout } from "./UserMenuLayout"
import type { UserMenuImplementationProps, UserMenuProps } from "./userMenu.model"

export const AdherentMenu = (props: UserMenuImplementationProps) => {
  const theme = useTheme()
  const userFullName = useSsoService().getFullName()
  const { accessStatus, isLoading, linkLoading } = useCommitteeStatus()

  const adhItems = useMemo(() => {
    const items: UserMenuProps["items"] = [
      {
        Icon: User,
        iconColor: theme.palette.common.blueGreenDark,
        label: "Mon compte",
        to: "/mon-compte",
      },
    ]

    if (accessStatus && !isLoading) {
      items.push({
        Icon: AdherentCommitteeIcon,
        iconColor: theme.palette.common.blueGreenDark,
        label: "Ma plateforme CSE",
        onClick: () => {},
        component: AdherentCommitteeLink,
        disabled: linkLoading,
      })
    }

    return items
  }, [accessStatus, isLoading, theme.palette, linkLoading])

  return (
    <UserMenuLayout {...props} items={adhItems}>
      <Stack direction="row" sx={{ p: 2 }}>
        <Stack direction="row" gap={2} width="100%">
          <Stack height="100%" alignItems="flex-start">
            <ImageContainer>
              <img
                alt="pharm_upp"
                src="/logos/pharmUpp/logo_pharmUpp_mini.svg"
                width="100%"
                height="100%"
                slot="icon"
              />
            </ImageContainer>
          </Stack>
          <Stack width="100%" gap={2} divider={<Divider />}>
            <Stack gap={1}>
              <UserName>{userFullName}</UserName>
              <SecureAccessStatusLabel />
              <ResetSecureAccessButton />
            </Stack>
            <MenuAdherentInformation {...props} />
          </Stack>
        </Stack>
      </Stack>
    </UserMenuLayout>
  )
}

const MenuAdherentInformation = ({ onClose }: UserMenuImplementationProps) => {
  const { summary: adherent } = useMyAdherentSummary()

  return (
    <Stack gap={1}>
      {!adherent ? (
        <>
          <Skeleton />
          <Skeleton />
        </>
      ) : (
        <>
          <AdherentName>{adherent.name}</AdherentName>
          <CBID>ID : {adherent.centralBusinessId}</CBID>
          <MembersLink membersCount={adherent.activeUsersCount} onClose={onClose} />
        </>
      )}
    </Stack>
  )
}

const SecureAccessStatusLabel = () => {
  const { getStepUpToken } = useStepUpToken()
  const theme = useTheme()

  const config = getStepUpToken()
    ? {
        Icon: Unlock,
        color: theme.palette.success.main,
        label: "Accès ouvert",
        description:
          "Vous avez saisi votre code d'accès, vos données sensibles sont actuellement visibles.",
      }
    : {
        Icon: Lock,
        color: undefined,
        label: "Accès restreint",
        description:
          "Vos données sensibles sont actuellement protégées, un code sera à saisir.",
      }

  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Typography
        fontSize={theme.typography.pxToRem(12)}
        color={config.color}
        display="flex"
      >
        <config.Icon
          size={14}
          color={config.color}
          style={{ marginRight: theme.spacing(0.5) }}
        />
        {config.label}
      </Typography>
      <Tooltip
        title={
          <Typography variant="12px" color={theme.palette.grey.dark}>
            <Typography fontSize="inherit" component="span" fontWeight="bold">
              {config.label}
            </Typography>{" "}
            : {config.description}
          </Typography>
        }
      >
        <Info size={12} color={theme.palette.text.disabled} />
      </Tooltip>
    </Stack>
  )
}

const ResetSecureAccessButton = () => {
  const token = useStepUpToken()
  const navigate = useNavigate()

  if (token.getStepUpToken()) {
    return (
      <Button
        size="small"
        sx={{
          boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.1)",
        }}
        onClick={() => {
          token.setStepUpToken(undefined)
          // soft refresh page
          navigate("/")
          navigate(-1)
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          pr={0.5}
        >
          <Stack
            direction="row"
            gap={0.5}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              width={20}
              height={20}
              borderRadius={10}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="#e6f9ff"
            >
              <ArrowDownRightIcon color="#348ba9" size={12} />
            </Box>
            <Typography component="span" variant="12px">
              Réactiver l’accès restreint
            </Typography>
          </Stack>
          <Lock size={12} />
        </Stack>
      </Button>
    )
  }

  return null
}

const IMG_SIZE = 60

const ImageContainer = styled(Box)(() => ({
  width: IMG_SIZE,
  height: IMG_SIZE,
  borderRadius: IMG_SIZE / 2,
  boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
}))

const UserName = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark1,
  fontSize: theme.typography.pxToRem(16),
  lineHeight: theme.typography.pxToRem(16),
  fontWeight: "bold",
}))

const AdherentName = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark1,
  fontSize: theme.typography.pxToRem(16),
  lineHeight: theme.typography.pxToRem(18),
  fontWeight: 500,
}))

const CBID = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey.dark,
  fontSize: theme.typography.pxToRem(12),
  lineHeight: theme.typography.pxToRem(12),
  fontWeight: 500,
  marginTop: 2,
}))

const MembersLink = ({
  membersCount,
  onClose,
}: { membersCount: number; onClose: () => void }) => {
  const {
    palette: { common },
  } = useTheme()
  return (
    <ButtonLink
      to="/mon-compte/mes-utilisateurs"
      variant="contained"
      size="small"
      onClick={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "fit-content",
        bgcolor: alpha(common.blueGreen2, 0.2),
      }}
    >
      <Typography variant="12px" color={common.blueGreenDark}>
        {`${membersCount} ${pluralize(membersCount, "membre", "membres")}`}
      </Typography>
      <ChevronRight size={15} color={alpha(common.blueGreenDark, 0.6)} />
    </ButtonLink>
  )
}
